import React, { useState, useEffect, useMemo, useRef } from "react";
import { MessageCircle, CheckCircle2, Search, X } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { company } from "../mock";

const fmt = new Intl.NumberFormat("it-IT");
const price = (n) => (n != null ? `\u20ac ${fmt.format(n)}` : "Su richiesta");

const empty = { nome: "", telefono: "", email: "", cf: "", indirizzo: "", messaggio: "" };

const PreventivoDialog = ({ engine, open, onClose, catalog = [] }) => {
  const [form, setForm] = useState(empty);
  const [sent, setSent] = useState(false);
  const [selEngine, setSelEngine] = useState(null);
  const [q, setQ] = useState("");
  const [showList, setShowList] = useState(false);
  const boxRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open) {
      setForm(empty);
      setSent(false);
      setSelEngine(engine || null);
      setQ("");
      setShowList(false);
    }
  }, [open, engine]);

  // close suggestions when clicking outside
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setShowList(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (t.length < 2) return [];
    return catalog
      .filter((m) => `${m.titolo} ${m.codice} ${m.marca}`.toLowerCase().includes(t))
      .slice(0, 8);
  }, [q, catalog]);

  const submit = (e) => {
    e.preventDefault();
    const eng = selEngine;
    const lines = [
      "*Richiesta preventivo motore*",
      eng ? `Motore: ${eng.titolo}` : (q.trim() ? `Ricerca: ${q.trim()}` : ""),
      eng && eng.codice ? `Codice: ${eng.codice}` : "",
      eng ? `Prezzo: ${price(eng.prezzo)}` : "",
      "\u2014\u2014\u2014",
      `Nome: ${form.nome}`,
      `Telefono: ${form.telefono}`,
      `Email: ${form.email}`,
      form.cf ? `C.F./P.IVA: ${form.cf}` : "",
      form.indirizzo ? `Indirizzo spedizione: ${form.indirizzo}` : "",
      form.messaggio ? `Messaggio: ${form.messaggio}` : "",
    ].filter(Boolean);
    const text = encodeURIComponent(lines.join("\n"));
    window.open(`${company.whatsappLink}?text=${text}`, "_blank");
    setSent(true);
  };

  const inputCls = "w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#0f2846] focus:ring-2 focus:ring-[#0f2846]/10 transition";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden gap-0 max-h-[92vh] overflow-y-auto">
        {sent ? (
          <div className="p-8 text-center">
            <CheckCircle2 size={52} className="text-[#22c35e] mx-auto" />
            <h3 className="font-display font-bold text-[#0f2846] text-xl mt-4">Ti stiamo reindirizzando su WhatsApp</h3>
            <p className="text-slate-500 mt-2 text-sm">Grazie {form.nome || ""}! Invia il messaggio precompilato per ricevere il tuo preventivo. Ti rispondiamo in giornata.</p>
            <button onClick={onClose} className="mt-6 bg-[#0f2846] hover:bg-[#14345a] text-white font-semibold px-6 py-2.5 rounded-lg transition-colors">Chiudi</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="bg-[#0f2846] text-white p-5">
              <h3 className="font-display font-bold text-lg">Richiedi preventivo</h3>
              {selEngine ? (
                <div className="mt-3 flex items-center gap-3 bg-white/5 rounded-xl p-2.5">
                  <img src={selEngine.foto} alt={selEngine.titolo} className="w-14 h-14 rounded-lg object-cover bg-white/10 shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{selEngine.titolo}</div>
                    <div className="text-white/60 text-xs">{selEngine.codice ? `Cod. ${selEngine.codice} \u00b7 ` : ""}<b className="text-[#7fdca3]">{price(selEngine.prezzo)}</b></div>
                  </div>
                  <button type="button" onClick={() => { setSelEngine(null); setQ(""); }} className="text-white/60 hover:text-white shrink-0" aria-label="Cambia motore"><X size={18} /></button>
                </div>
              ) : (
                <div className="mt-3" ref={boxRef}>
                  <label className="block text-xs text-white/70 mb-1.5">Trova il tuo motore <span className="text-white/40">(modello auto o codice motore)</span></label>
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={q}
                      onChange={(e) => { setQ(e.target.value); setShowList(true); }}
                      onFocus={() => setShowList(true)}
                      placeholder="Es. Fiat Ducato, Audi A4, 04L100091C..."
                      className="w-full rounded-lg bg-white text-slate-800 pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-white/40 transition"
                    />
                    {showList && matches.length > 0 && (
                      <div className="absolute z-10 left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-100 max-h-64 overflow-y-auto">
                        {matches.map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => { setSelEngine(m); setShowList(false); }}
                            className="w-full flex items-center gap-3 p-2.5 hover:bg-slate-50 text-left border-b border-slate-50 last:border-0"
                          >
                            <img src={m.foto} alt="" className="w-10 h-10 rounded-md object-cover bg-slate-100 shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            <div className="min-w-0">
                              <div className="text-xs font-semibold text-[#0f2846] truncate">{m.titolo}</div>
                              <div className="text-[11px] text-slate-400">{m.codice ? `Cod. ${m.codice} \u00b7 ` : ""}{price(m.prezzo)}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {showList && q.trim().length >= 2 && matches.length === 0 && (
                      <div className="absolute z-10 left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-100 p-3 text-xs text-slate-400">
                        Nessun motore trovato. Descrivi la tua richiesta nel messaggio: ti aiutiamo noi.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="p-5 space-y-3.5">
              <div className="grid sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Nome e cognome</label>
                  <input required value={form.nome} onChange={set("nome")} className={inputCls} placeholder="Mario Rossi" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Telefono</label>
                  <input required value={form.telefono} onChange={set("telefono")} className={inputCls} placeholder="333 1234567" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">Email</label>
                <input type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="nome@email.com" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">C.F./P.IVA <span className="text-slate-400 font-normal">(opzionale)</span></label>
                  <input value={form.cf} onChange={set("cf")} className={inputCls} placeholder="Codice fiscale o P.IVA" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Indirizzo di spedizione <span className="text-slate-400 font-normal">(opzionale)</span></label>
                  <input value={form.indirizzo} onChange={set("indirizzo")} className={inputCls} placeholder="Via, CAP, città" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">Messaggio <span className="text-slate-400 font-normal">(facoltativo)</span></label>
                <textarea rows={3} value={form.messaggio} onChange={set("messaggio")} className={`${inputCls} resize-none`} placeholder="Es. verifica compatibilità, tempi di consegna..." />
              </div>
              <button type="submit" className="w-full flex items-center justify-center gap-2 bg-[#22c35e] hover:bg-[#1da851] text-white font-semibold py-3.5 rounded-lg transition-all hover:-translate-y-0.5 shadow-lg shadow-[#22c35e]/25">
                <MessageCircle size={17} /> Invia e contatta su WhatsApp
              </button>
              <p className="text-center text-slate-400 text-xs">Compila il modulo e verrai reindirizzato su WhatsApp con la richiesta pronta.</p>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PreventivoDialog;
